import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Table, Spin } from 'antd';
import { useDebounce } from '../../helper/performanceHooks';
import './virtualizedTable.scss';

interface VirtualizedTableProps {
  dataSource: any[];
  columns: any[];
  rowHeight?: number;
  visibleRows?: number;
  loading?: boolean;
  className?: string;
  rowKey?: string | ((record: any) => string);
  onRowClick?: (record: any) => void;
  scrollY?: number;
  containerHeight?: number;
}

/**
 * VirtualizedTable - A performance-optimized table for large datasets
 * 
 * Instead of rendering all rows, it only renders visible rows + buffer
 * This dramatically improves performance for lists with 1000+ items
 */
const VirtualizedTable: React.FC<VirtualizedTableProps> = ({
  dataSource,
  columns,
  rowHeight = 54,
  visibleRows = 15,
  loading = false,
  className = '',
  rowKey = 'id',
  onRowClick,
  scrollY,
  containerHeight = 600,
}) => {
  // Use containerHeight if provided, otherwise fall back to scrollY
  const height = containerHeight || scrollY || 600;
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedScrollTop = useDebounce(scrollTop, 16); // ~60fps

  // Calculate which rows to render
  const { visibleData, startIndex, endIndex, totalHeight, offsetY } = useMemo(() => {
    if (!dataSource || dataSource.length === 0) {
      return {
        visibleData: [],
        startIndex: 0,
        endIndex: 0,
        totalHeight: 0,
        offsetY: 0,
      };
    }

    const totalHeight = dataSource.length * rowHeight;
    const bufferRows = 5; // Extra rows above/below viewport for smooth scrolling
    
    const startIndex = Math.max(0, Math.floor(debouncedScrollTop / rowHeight) - bufferRows);
    const endIndex = Math.min(
      dataSource.length,
      Math.ceil((debouncedScrollTop + height) / rowHeight) + bufferRows
    );
    
    const visibleData = dataSource.slice(startIndex, endIndex);
    const offsetY = startIndex * rowHeight;

    return { visibleData, startIndex, endIndex, totalHeight, offsetY };
  }, [dataSource, debouncedScrollTop, rowHeight, height]);

  // Handle scroll events
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setScrollTop(target.scrollTop);
  }, []);

  // Add index to visible data for proper key handling
  const indexedData = useMemo(() => {
    return visibleData.map((item, idx) => ({
      ...item,
      _virtualIndex: startIndex + idx,
    }));
  }, [visibleData, startIndex]);

  // Handle row key generation
  const getRowKey = useCallback(
    (record: any) => {
      if (typeof rowKey === 'function') {
        return rowKey(record);
      }
      return record[rowKey] || record._virtualIndex;
    },
    [rowKey]
  );

  if (loading) {
    return (
      <div className="virtualized-table-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={`virtualized-table-container ${className}`}>
      <div
        ref={containerRef}
        className="virtualized-table-scroll-container"
        style={{ height: height, overflowY: 'auto' }}
        onScroll={handleScroll}
      >
        {/* Spacer to maintain scroll height */}
        <div style={{ height: totalHeight, position: 'relative' }}>
          {/* Positioned content */}
          <div
            style={{
              position: 'absolute',
              top: offsetY,
              left: 0,
              right: 0,
            }}
          >
            <Table
              rowKey={getRowKey}
              dataSource={indexedData}
              columns={columns}
              pagination={false}
              showHeader={startIndex === 0} // Only show header when at top
              className="virtualized-inner-table"
              onRow={(record) => ({
                onClick: () => onRowClick?.(record),
              })}
            />
          </div>
        </div>
      </div>
      
      {/* Fixed header that stays visible */}
      {startIndex > 0 && (
        <div className="virtualized-fixed-header">
          <Table
            rowKey={getRowKey}
            dataSource={[]}
            columns={columns}
            pagination={false}
            className="virtualized-header-table"
          />
        </div>
      )}
      
      {/* Stats bar */}
      <div className="virtualized-table-stats">
        Showing {startIndex + 1} - {Math.min(endIndex, dataSource.length)} of {dataSource.length} items
      </div>
    </div>
  );
};

export default VirtualizedTable;
